/**
 * Main entry point for Daily Network
 * Modular, performance-optimized, production-ready
 */

import './styles/main.css';
import { initErrorBoundary } from './core/errorBoundary.js';
import { ParticleScene } from './three/scene.js';
import { isTouchDevice, prefersReducedMotion } from './core/utils.js';
import {
  CustomCursor,
  SmoothScroll,
  ScrollReveal,
  IntroAnimation,
  revealHero,
  Typewriter,
  EasterEgg,
  CardTilt,
  RippleEffect,
  ParticleBurst,
  SiteViewer,
  FundingForm
} from './ui/index.js';
import { Storage } from './db/storage.js';
import { Router } from './router/index.js';
import { DynamicPage } from './pages/DynamicPage.js';

/**
 * Application class manages all modules
 */
class Application {
  #modules = new Map();
  #scene = null;
  #isInitialized = false;
  #router = null;
  #dynamicPage = null;
  #homeSections = null;
  #dynamicContainer = null;

  constructor() {
    // Initialize error boundary first
    this.#modules.set('errorBoundary', initErrorBoundary());
    
    // Setup router
    this.#router = new Router((type, slug, data, anchor) => {
      this.handleRoute(type, slug, data, anchor);
    });
    
    // Setup dynamic page renderer
    this.#dynamicPage = new DynamicPage('page-container');
  }

  async init() {
    if (this.#isInitialized) return;

    try {
      // Initialize storage from backend
      await Storage.init();

      // Wait for fonts and critical resources
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      // Start intro animation
      await this.#runIntro();

      // Initialize modules after intro
      this.#initModules();

      // Start Three.js scene
      this.#initScene();

      this.#isInitialized = true;
      
      console.log('[App] Initialized successfully');

    } catch (error) {
      console.error('[App] Initialization error:', error);
      this.#modules.get('errorBoundary')?.show();
    }
  }

  #runIntro() {
    return new Promise((resolve) => {
      const intro = new IntroAnimation(() => {
        // Reveal hero content after intro
        revealHero();
        resolve();
      });
      
      intro.start();
    });
  }

  #initModules() {
    // Setup dynamic content FIRST (before smooth scroll attaches handlers)
    this.renderDynamicNavbar();
    this.renderDynamicFooterLinks();
    this.renderDynamicCards();
    this.renderDynamicMilestones();
    this.renderInfraSection();

    // Re-render dynamic content when localStorage changes in other tabs (admin updates)
    window.addEventListener('storage', (e) => {
      const keys = ['daily_navbar_items', 'daily_protocol_cards', 'daily_milestones', 'daily_infrastructure', 'daily_funding_requests'];
      if (!e.key || keys.includes(e.key)) {
        this.renderDynamicNavbar();
        this.renderDynamicFooterLinks();
        this.renderDynamicCards();
        this.renderDynamicMilestones();
        this.renderInfraSection();
      }
    });
    
    // Smooth scroll (core functionality) - attaches click handlers to anchor links
    const scroll = new SmoothScroll();
    this.#modules.set('scroll', scroll);

    // Custom cursor (desktop only)
    if (!isTouchDevice()) {
      this.#modules.set('cursor', new CustomCursor());
    }

    // Scroll reveal animations
    this.#modules.set('reveal', new ScrollReveal());

    // Typewriter effect
    this.#modules.set('typewriter', new Typewriter('heroSub', [
      "Daily Network on a quantum resilient mode – in the future we build and fuse quantum cryptography network with distributed ledger technology — creating an unbreakable, infinitely scalable network ecosystem for the next decade.",
      "Post-quantum consensus. Sub-second finality. Zero compromise on decentralization.",
      "The first blockchain built not to survive the quantum era — but to define it.",
      "128,000 TPS. Quantum-resistant. Fully decentralized. Welcome to Daily Network."
    ], { typeSpeed: 40, deleteSpeed: 20, pauseDuration: 3200 }));

    // Easter egg
    this.#modules.set('easterEgg', new EasterEgg());

    // Visual effects (disabled for reduced motion)
    if (!prefersReducedMotion()) {
      this.#modules.set('cardTilt', new CardTilt());
      this.#modules.set('ripple', new RippleEffect());
      this.#modules.set('particleBurst', new ParticleBurst());
    }
    
    // Site viewer for infrastructure links
    this.#modules.set('siteViewer', new SiteViewer());
    
    // Funding form
    this.#modules.set('fundingForm', new FundingForm());
    
    // Handle initial route
    this.handleInitialRoute();
  }

  #initScene() {
    const canvas = document.getElementById('three-canvas');
    if (!canvas) return;

    // Check WebGL support
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    if (!gl) {
      console.warn('[App] WebGL not supported, skipping scene');
      return;
    }

    // Adjust config for mobile
    const isMobile = isTouchDevice() || window.innerWidth < 768;
    const config = {
      particleCount: isMobile ? 600 : 1200,
      maxConnections: isMobile ? 80 : 150,
      connectionDistance: 4.5,
      cellSize: 5
    };

    this.#scene = new ParticleScene(canvas, config);
    
    // Connect scene to scroll
    const scroll = this.#modules.get('scroll');
    if (scroll) {
      scroll.onScroll((y) => {
        this.#scene?.setScroll(y);
      });
    }

    // Connect scene to mouse
    document.addEventListener('mousemove', (e) => {
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = -(e.clientY / window.innerHeight) * 2 + 1;
      this.#scene?.setMouse(x, y);
    }, { passive: true });

    // Start rendering
    this.#scene.start();
  }

  /**
   * Cleanup all modules
   */
  renderDynamicNavbar() {
    const navItems = Storage.getNavbarItems();
    const navContainer = document.getElementById('mainNav');
    
    if (!navContainer) return;
    
    const hasInfra = Storage.hasActiveInfrastructure();
    
    navContainer.innerHTML = navItems.filter(item => {
      if (!item.isActive) return false;
      if (item.slug === 'infrastructure' && !hasInfra) return false;
      return true;
    }).map((item, index) => {
      const delay = ((index + 1) * 0.1).toFixed(1);
      const isExternal = item.type === 'external';
      const isDefault = item.isDefault || false;
      const isDynamicInternal = item.type === 'internal' && !isDefault;
      
      const target = isExternal ? `target="${item.target || '_blank'}" rel="noopener noreferrer"` : '';
      
      return `
        <li role="none">
          <a href="${item.url}" 
             class="magnetic nav-link visible" 
             role="menuitem" 
             style="--delay:${delay}s"
             data-type="${item.type}"
             data-slug="${item.slug}"
             data-default="${isDefault}"
             data-dynamic="${isDynamicInternal}"
             ${target}>
            ${item.title}
          </a>
        </li>
      `;
    }).join('');
    
    // Attach router click handlers for nav items
    navContainer.querySelectorAll('a.nav-link').forEach(link => {
      link.addEventListener('click', (e) => {
        const type = link.dataset.type;
        const slug = link.dataset.slug;
        const isDynamic = link.dataset.dynamic === 'true';
        const url = link.getAttribute('href');

        if (type === 'internal' && isDynamic) {
          e.preventDefault();
          this.#router.goToPage(slug);
        } else if (type === 'section') {
          const homeSections = document.getElementById('home-sections');
          // If we are on a dynamic page, force navigation to home with hash
          if (homeSections && homeSections.style.display === 'none') {
            e.preventDefault();
            window.history.pushState({}, '', '/' + url);
            window.dispatchEvent(new Event('popstate'));
            
            setTimeout(() => {
              const target = document.querySelector(url);
              if (target) {
                const scroll = this.#modules.get('scroll');
                if (scroll && typeof scroll.scrollTo === 'function') {
                  scroll.scrollTo(target.offsetTop);
                } else {
                  window.scrollTo({ top: target.offsetTop, behavior: 'smooth' });
                }
              }
            }, 100);
          }
        }
      });
    });
  }

  renderDynamicFooterLinks() {
    const navItems = Storage.getNavbarItems();
    const footerContainer = document.getElementById('footerNav');

    if (!footerContainer) return;

    const hasInfra = Storage.hasActiveInfrastructure();
    const escapeHtml = (str) =>
      String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    footerContainer.innerHTML = navItems.filter(item => {
      if (!item.isActive) return false;
      if (item.slug === 'infrastructure' && !hasInfra) return false;
      return true;
    }).map((item) => {
      const isExternal = item.type === 'external';
      const isDefault = item.isDefault || false;
      const isDynamicInternal = item.type === 'internal' && !isDefault;
      const target = isExternal ? `target="${escapeHtml(item.target || '_blank')}" rel="noopener noreferrer"` : '';

      return `
        <li>
          <a href="${escapeHtml(item.url)}"
             class="footer-link"
             data-type="${escapeHtml(item.type)}"
             data-slug="${escapeHtml(item.slug)}"
             data-dynamic="${isDynamicInternal}"
             ${target}>
            ${escapeHtml(item.title)}
          </a>
        </li>
      `;
    }).join('');

    footerContainer.querySelectorAll('a.footer-link').forEach(link => {
      link.addEventListener('click', (e) => {
        const type = link.dataset.type;
        const slug = link.dataset.slug;
        const isDynamic = link.dataset.dynamic === 'true';
        const url = link.getAttribute('href');

        if (type === 'internal' && isDynamic) {
          e.preventDefault();
          this.#router.goToPage(slug);
        } else if (type === 'section') {
          const homeSections = document.getElementById('home-sections');
          if (homeSections && homeSections.style.display === 'none') {
            e.preventDefault();
            window.history.pushState({}, '', '/' + url);
            window.dispatchEvent(new Event('popstate'));

            setTimeout(() => {
              const target = document.querySelector(url);
              if (target) {
                const scroll = this.#modules.get('scroll');
                if (scroll && typeof scroll.scrollTo === 'function') {
                  scroll.scrollTo(target.offsetTop);
                } else {
                  window.scrollTo({ top: target.offsetTop, behavior: 'smooth' });
                }
              }
            }, 100);
          }
        }
      });
    });
  }

  renderInfraSection() {
    const items = Storage.getActiveInfrastructure();
    const container = document.getElementById('infraGrid');
    const section = document.getElementById('infrastructure');

    if (!container || !section) return;

    if (items.length === 0) {
      container.innerHTML = '';
      section.style.padding = '40px 0';
      section.querySelector('.section-label').style.display = 'none';
      section.querySelector('.section-title').style.display = 'none';
      return;
    }

    section.style.padding = '';
    section.querySelector('.section-label').style.display = '';
    section.querySelector('.section-title').style.display = '';

    // Escape HTML to prevent attribute-breaking values
    const escapeHtml = (str) =>
      String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const renderCard = (item, index) => {
      const hasImage = item.image && item.image.trim() !== '';
      const safeName = escapeHtml(item.name);
      const safeDesc = escapeHtml(item.description);
      const safeTag = escapeHtml(item.category || 'Ecosystem');
      const safeUrl = escapeHtml(item.url);
      const safeImage = hasImage ? escapeHtml(item.image) : '';
      const bgStyle = hasImage
        ? `style="background-image: url(${safeImage}); background-size: cover; background-position: center;"`
        : '';
      return `
        <article class="project-card infrastructure-card reveal reveal-delay-${index + 1}" data-tilt tabindex="0" role="article" data-infra-name="${safeName}" data-infra-url="${safeUrl}">
          <div class="project-card-bg" aria-hidden="true" ${bgStyle}></div>
          <div class="project-card-inner">
            <div class="project-tag">${safeTag}</div>
            <h3 class="project-name">${safeName}</h3>
            <p class="project-desc">${safeDesc}</p>
          </div>
          <div class="view-btn magnetic" aria-hidden="true">↗</div>
        </article>
      `;
    };

    const categories = [...new Set(items.map((item) => item.category).filter(Boolean))];
    const uncategorized = items.filter((i) => !i.category);

    if (categories.length > 0 || uncategorized.length > 0) {
      const parts = [];

      // Categorized groups
      categories.forEach((cat) => {
        const catItems = items.filter((i) => i.category === cat);
        const safeCat = escapeHtml(cat);
        parts.push(`
          <div class="infra-category" style="grid-column: 1 / -1;">
            <h3 class="infra-cat-title" style="font-size: 24px; color: var(--gold2); margin-bottom: 24px; border-bottom: 1px solid var(--border); padding-bottom: 8px;">${safeCat}</h3>
            <div class="projects-grid">
              ${catItems.map(renderCard).join('')}
            </div>
          </div>
        `);
      });

      // Uncategorized group (rendered after categories)
      if (uncategorized.length > 0) {
        parts.push(`
          <div class="infra-category" style="grid-column: 1 / -1;">
            <h3 class="infra-cat-title" style="font-size: 24px; color: var(--gold2); margin-bottom: 24px; border-bottom: 1px solid var(--border); padding-bottom: 8px;">Other</h3>
            <div class="projects-grid">
              ${uncategorized.map(renderCard).join('')}
            </div>
          </div>
        `);
      }

      container.innerHTML = parts.join('');
    } else {
      container.innerHTML = `
        <div class="projects-grid" style="grid-column: 1 / -1;">
          ${items.map(renderCard).join('')}
        </div>
      `;
    }

    // Event delegation: one listener on the container handles all card clicks/keyboard
    if (!container.dataset.infraDelegated) {
      container.dataset.infraDelegated = 'true';

      container.addEventListener('click', (e) => {
        const card = e.target.closest('.infrastructure-card');
        if (card) this.#openInfraCard(card);
      });

      container.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const card = e.target.closest('.infrastructure-card');
        if (card) {
          e.preventDefault();
          this.#openInfraCard(card);
        }
      });
    }

    // Re-initialize card tilt effect if available
    if (this.#modules.has('cardTilt')) {
      this.#modules.get('cardTilt').refresh();
    }
  }

  #openInfraCard(card) {
    const normalizeUrl = (rawUrl) => {
      const value = (rawUrl || '').trim();
      if (!value) return '';
      if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value)) return value;
      if (value.startsWith('//')) return `https:${value}`;
      if (value.startsWith('#') || value.startsWith('/')) return value;
      return `https://${value}`;
    };

    const name = card.dataset.infraName || '';
    const url = normalizeUrl(card.dataset.infraUrl || '');
    if (!url) {
      console.warn('[Infra] Card clicked but no URL set:', name || card);
      return;
    }

    const siteViewer = this.#modules.get('siteViewer');
    if (siteViewer && typeof siteViewer.open === 'function') {
      try {
        siteViewer.open(name, url);
        return;
      } catch (err) {
        console.warn('[Infra] SiteViewer failed, falling back to direct open:', err);
      }
    }

    // Fallback: open directly
    if (/^https?:/i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = url;
    }
  }

  renderDynamicCards() {
    const cards = Storage.getCards();
    const cardsContainer = document.getElementById('protocolGrid');
    
    if (!cardsContainer) return;
    
    cardsContainer.innerHTML = cards.filter(card => card.isActive).map((card, index) => {
      const delay = ((index + 1) * 0.1).toFixed(1);
      const hasImage = card.image && card.image.trim() !== '';
      const target = card.buttonLink && card.buttonLink.startsWith('http') ? '_blank' : '_self';
      
      return `
        <article class="project-card reveal reveal-delay-${index + 1}" data-tilt tabindex="0" role="article" aria-labelledby="card-${card.id}" style="cursor: pointer;" onclick="if('${card.buttonLink}' && '${card.buttonLink}' !== 'undefined') { if('${target}' === '_blank') window.open('${card.buttonLink}', '_blank'); else window.location.href='${card.buttonLink}'; }">
          <div class="project-card-bg" aria-hidden="true" ${hasImage ? `style="background-image: url(${card.image}); background-size: cover; background-position: center;"` : ''}></div>
          <div class="project-card-inner">
            <div class="project-tag">${card.tag || ''}</div>
            <h3 class="project-name" id="card-${card.id}">${card.title || 'Untitled Phase'}</h3>
            <p class="project-desc">${card.description || 'No description available'}</p>
          </div>
          <div class="view-btn magnetic" aria-hidden="true">${card.buttonText || '↗'}</div>
        </article>
      `;
    }).join('');

    // Re-initialize card tilt effect if available
    if (this.#modules.has('cardTilt')) {
      this.#modules.get('cardTilt').refresh();
    }
  }

  renderDynamicMilestones() {
    const data = Storage.getMilestones();
    const section = document.getElementById('experience');
    
    if (!section) return;
    
    // Update section label
    const labelEl = section.querySelector('.section-label');
    if (labelEl) labelEl.textContent = data.sectionLabel || 'Milestones';
    
    // Update heading
    const headingEl = section.querySelector('.section-title');
    if (headingEl) {
      headingEl.innerHTML = (data.heading || 'Building the quantum future').replace(/\n/g, '<br>');
    }
    
    // Update background image
    if (data.backgroundImage) {
      section.style.backgroundImage = `url(${data.backgroundImage})`;
    }
    
    // Update timeline items
    const timeline = section.querySelector('.timeline');
    if (timeline) {
      const items = data.items?.filter(i => i.isActive) || [];
      
      if (items.length === 0) {
        timeline.innerHTML = '<p style="text-align: center; color: var(--text-dim); padding: 40px 0;">No milestones available.</p>';
      } else {
        timeline.innerHTML = items.map((item) => `
          <article class="timeline-item" data-reveal role="listitem">
            <div class="timeline-dot" aria-hidden="true"></div>
            <time class="timeline-year">${item.period}</time>
            <h3 class="timeline-role">${item.title}</h3>
            <div class="timeline-company">${item.subtitle || ''}</div>
            <p class="timeline-desc">${item.description}</p>
          </article>
        `).join('');
      }
    }
  }

  handleRoute(type, slug, data, anchor) {
    this.#homeSections = document.getElementById('home-sections');
    this.#dynamicContainer = document.getElementById('page-container');
    
    if (!this.#homeSections || !this.#dynamicContainer) return;
    
    switch (type) {
      case 'home':
        this.#homeSections.style.display = 'block';
        this.#dynamicContainer.style.display = 'none';
        this.#dynamicPage.hide();
        if (anchor) {
          setTimeout(() => {
            document.querySelector(anchor)?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else {
          window.scrollTo(0, 0);
        }
        break;
        
      case 'page':
        this.#homeSections.style.display = 'none';
        this.#dynamicContainer.style.display = 'block';
        this.#dynamicPage.render(data);
        break;

      case '404':
        this.#homeSections.style.display = 'none';
        this.#dynamicContainer.style.display = 'block';
        this.#dynamicContainer.innerHTML = `
          <div class="page-error" style="text-align: center; padding: 100px 20px;">
            <h1 style="font-size: 48px; color: var(--gold2); margin-bottom: 20px;">404</h1>
            <p style="color: var(--text-dim); margin-bottom: 30px;">Page not found</p>
            <button onclick="window.location.href='/'" class="btn btn-primary">Go Home</button>
          </div>
        `;
        break;
    }
  }

  handleInitialRoute() {
    const path = window.location.pathname.replace(/^\//, '');
    if (path) {
      this.#router.handleRoute('/' + path);
    }
  }

  destroy() {
    // Stop Three.js
    this.#scene?.stop();
    this.#scene?.destroy();
    
    // Destroy UI modules
    for (const [name, module] of this.#modules) {
      if (name !== 'errorBoundary' && typeof module?.destroy === 'function') {
        module.destroy();
      }
    }
    
    // Destroy router and page renderer
    this.#router?.destroy();
    this.#dynamicPage?.destroy();
    
    this.#modules.clear();
    this.#isInitialized = false;
  }
}

// Initialize when DOM is ready
const app = new Application();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => app.init());
} else {
  app.init();
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  app.destroy();
});

// Expose for debugging (dev only)
if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  window.__APP__ = app;
}
